import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { injectIntl, intlShape, FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import { Row, Col, Card, CardBody, FormGroup, Input, Button, ListGroup, ListGroupItem, Label, Alert, Collapse } from 'reactstrap';
import FontAwesome from 'react-fontawesome';

import moment from 'moment';
import randomstring from 'randomstring';

import config from '../../../../config';

import FontCustom from '../../../../components/Content/FontCustom';

import { getCartItems, getCartShipping, getCartVoucher, getCartSubscription, isCartValid, getCartTotals, trackAnalytics } from '../../CartActions';
import { getShippingFeeByAddress } from '../../../ShippingFee/ShippingFeeActions';
import { createOrderRequest, getPaymentMethods } from '../../../Order/OrderActions';
import { editSubscriptionRequest } from '../../../Subscription/SubscriptionActions';
import { getInvoiceAddress } from '../../../Address/AddressActions';
import { getLoggedUser } from '../../../User/UserActions';
import { getLanguagePath } from '../../../Intl/IntlActions';
import { displayErrors } from '../../../Error/ErrorActions';


import CartItem from '../item/CartItem';
import CartTotals from './CartTotals';
import AddressView from '../../../Address/components/view/AddressView';
import CmsPanel from '../../../Cms/components/content/CmsPanel';
import OgonePayment from '../../../Order/components/payment/OgonePayment';
import PaypalPayment from '../../../Order/components/payment/PaypalPayment';
import WorldlinePayment from '../../../Order/components/payment/WorldlinePayment';


class CartPayment extends Component {
    constructor(props) {
        super(props);

        this.state = {
            method: 'creditcard',
            toggle: '',
            order: null,
            payment: null,
            subscription: null,
            isCreatingOrder: false,
        };
    }

    getLegalConditionsUrl() {
        let slug;
        switch(this.props.intl.locale) {
            case 'fr':
                slug = 'conditions-generales-vente';
                break;

            case 'en':
            default:
                slug = 'legal-conditions';
        }

        return getLanguagePath(`/${slug}`, this.props.intl.locale);
    }

    createSubscription = () => {
        if(this.props.subscription && this.state.method !== 'banktransfer') {
            return this.props.dispatch(editSubscriptionRequest({
                ...this.props.subscription,
                user: this.props.user._id,
                items: this.props.items,
                periodTrigger: this.props.subscription.periodIntervalUnit === 'week' ? moment.utc().format('d') : moment.utc().format('D'),
                flags: [this.state.method],
            }));
        }
        return Promise.resolve(null);
    }

    createOrder = (payment, subscription) => {
        return new Promise((resolve, reject) => {
            this.setState({
                isCreatingOrder: true,
            });
            this.props.dispatch(createOrderRequest({
                subscription: subscription ? subscription._id : null,
                payments: [payment],
                items: this.props.items,
                totals: this.props.cartTotals,
                shippings: [{
                    id: randomstring.generate({ length: 8 }),
                    address: (this.props.shipping || {}).address,
                    carrier: this.props.shippingFee.carrier,
                    trackingUrl: this.props.shippingFee.trackingUrl,
                    status: 'pending',
                }],
                invoice: {
                    address: this.props.invoiceAddress,
                },
                vouchers: [this.props.voucher],
                country: ((this.props.shipping || {}).address || {}).countryCode,
                currency: this.props.currency,
                language: this.props.intl.locale,
            }))
            .then(resolve)
            .catch(err => {
                console.error(err);
                displayErrors('error', this.props.intl.formatMessage({
                    id: 'orderCreationError',
                    defaultMessage: 'We can not create your order for the moment...Thanks to contact us!',
                }));
                reject(err);
            }).finally(() => {
                this.setState({
                    isCreatingOrder: false,
                });
            });
        });
    }

    handleValidOrder = () => {
        const payment = {
            id: randomstring.generate({ length: 8 }),
            method: this.state.method,
            status: 'pending',
            amount: this.props.cartTotals ? this.props.cartTotals.totalTTC : 0,
            dates: {
                pending: moment.utc().format(),
            },
        };

        return this.createSubscription().then(subscription => {
            return this.createOrder(payment, subscription).then(order => {
                this.setState({
                    order,
                    payment,
                    subscription,
                    isProcessingPayment: true,
                }, () => {
                    if(this.state.method === 'banktransfer') {
                        trackAnalytics(order);
                        // Banktransfer is NOT processed by Ogone anymore, or uncomment line below
                        this.props.goToNextStep(null, order._id);
                    }
                });
                return { order, subscription, payment };
            }).catch(err => {
                return {};
            });
        }).catch(err => {
            return {};
        });
    }

    renderOrderPaymentProcessor() {
        if(['creditcard'].includes(this.state.method)) { // , 'banktransfer'
            const paymentProviderProps = {
                order: this.state.order,
                subscription: this.state.subscription,
                payment: this.state.payment,
                autoTrigger: true
            };
            return config.ogone.isReplaced ? <WorldlinePayment {...paymentProviderProps} /> : <OgonePayment {...paymentProviderProps} />;
        }
    }

    render() {
        const { items, shipping, subscription } = this.props;
        return (
            <Fragment>
                <div className="px-3 px-md-0">
                    <div className="my-3 py-2 d-flex justify-content-between text-uppercase border-bottom-info text-primary">
                        <span className="special-font text-lg font-weight-bold"><FormattedMessage id="orderMy" defaultMessage="My order" /></span>
                        <Link to={getLanguagePath('/cart/preview', this.props.intl.locale)} className="text-underline"><FormattedMessage id="edit" defaultMessage="Edit" /></Link>
                    </div>

                    <div className="my-3 py-3 border-bottom-info">
                        <Row>
                            <Col xs="12" sm="6">
                                {(items || []).map((item, index) => <CartItem key={item.productId || index} item={item} disableEdit />)}
                            </Col>
                            <Col>
                                <CartTotals />
                            </Col>
                        </Row>
                    </div>

                    <div className="my-3">
                        <Row>
                            <Col xs="12" sm={{ size: 4, offset: 2 }}>
                                <strong className="text-dark"><FormattedMessage id="cartDeliveryTitle" defaultMessage="Delivery" /></strong>
                            </Col>
                            <Col>
                                {
                                    shipping && shipping.address
                                    ? <AddressView address={shipping.address} inline />
                                    : <strong className="text-danger"><FontAwesome name="exclamation-circle" /> <FormattedMessage id="cartShippingAddressMissingError" defaultMessage="You did not set your shipping address!" /></strong>
                                }
                            </Col>
                        </Row>
                    </div>
                </div>


                <Card className="mt-3 py-3 text-primary">
                    <CardBody>
                        {
                            this.props.isCartValid
                            ? (
                                <Fragment>
                                    {(getPaymentMethods().includes('creditcard') || (this.props.user || {}).role === 'admin') && (
                                        <Card className="mb-2 border border-info">
                                            <CardBody className="p-2 p-lg-3">
                                                <Row noGutters className="mb-2">
                                                    <Col xs="12" md="8" className="mb-2">
                                                        <FormGroup check inline className="m-0">
                                                            <Label check className="special-font font-weight-bold">
                                                                <Input type="radio" name="method" checked={this.state.method === 'creditcard'} onChange={event => event.target.checked && this.setState({ method: 'creditcard' })} /> <FormattedMessage id="cartPaymentCreditCardLabel" defaultMessage="I pay by credit card" />
                                                            </Label>
                                                        </FormGroup>
                                                        <a href="#" onClick={event => { event.preventDefault(); this.setState({ toggle: 'creditcard' }); }}><FontCustom name="help" size="1.5" /></a>
                                                    </Col>
                                                    <Col className="text-center text-md-right">
                                                        <span className="text-info"><FontCustom name="cb" size="3" /><FontCustom name="visa" size="3" /><FontCustom name="mastercard" size="3" /></span>
                                                    </Col>
                                                </Row>
                                                <Collapse isOpen={this.state.toggle === 'creditcard'} className="text-center text-lg-left">
                                                    <CmsPanel slug="payment-creditcard-info" />
                                                </Collapse>
                                            </CardBody>
                                        </Card>
                                    )}

                                    {(getPaymentMethods().includes('paypal') || (this.props.user || {}).role === 'admin') && (
                                        <Card className="mb-2 border border-info">
                                            <CardBody className="p-2 p-lg-3">
                                                <Row noGutters className="mb-2">
                                                    <Col xs="12" md="8" className="mb-2">
                                                        <FormGroup check inline className="m-0">
                                                            <Label check className="special-font font-weight-bold">
                                                                <Input type="radio" name="method" checked={this.state.method === 'paypal'} onChange={event => event.target.checked && this.setState({ method: 'paypal' })} /> <FormattedMessage id="cartPaymentPaypalLabel" defaultMessage="I pay with Paypal" />
                                                            </Label>
                                                        </FormGroup>
                                                        <a href="#" onClick={event => { event.preventDefault(); this.setState({ toggle: 'paypal' }); }}><FontCustom name="help" size="1.5" /></a>
                                                    </Col>
                                                    <Col className="text-center text-md-right">
                                                        <span className="text-info"><img src={`${config.url}assets/images/paypal.png`} role="presentation" style={{ maxWidth: '70px' }} /></span>
                                                    </Col>
                                                </Row>
                                                <Collapse isOpen={this.state.toggle === 'paypal'} className="text-center text-lg-left">
                                                    <CmsPanel slug="payment-paypal-info" />
                                                </Collapse>
                                            </CardBody>
                                        </Card>
                                    )}

                                    {((getPaymentMethods().includes('banktransfer') && !subscription) || (this.props.user || {}).role === 'admin') && (
                                        <Card className="mb-2 border border-info">
                                            <CardBody className="p-2 p-lg-3">
                                                <Row noGutters className="mb-2">
                                                    <Col xs="12" md="8" className="mb-2">
                                                        <FormGroup check inline className="m-0">
                                                            <Label check className="special-font font-weight-bold">
                                                                <Input type="radio" name="method" checked={this.state.method === 'banktransfer'} onChange={event => event.target.checked && this.setState({ method: 'banktransfer' })} /> <FormattedMessage id="cartPaymentBankTransferLabel" defaultMessage="I pay by bank transfer" />
                                                            </Label>
                                                        </FormGroup>
                                                        <a href="#" onClick={event => { event.preventDefault(); this.setState({ toggle: 'banktransfer' }); }}><FontCustom name="help" size="1.5" /></a>
                                                    </Col>
                                                    <Col className="text-center text-md-right">
                                                        <span className="text-info"><FontCustom name="transfert" size="2" /></span>
                                                    </Col>
                                                </Row>
                                                <Collapse isOpen={this.state.toggle === 'banktransfer'} className="text-center text-lg-left">
                                                    <CmsPanel slug="payment-banktransfer-info" />
                                                </Collapse>
                                            </CardBody>
                                        </Card>
                                    )}

                                    {
                                        this.state.method === 'paypal'
                                        ? (
                                            <div className="mt-3 py-3">
                                                <PaypalPayment validOrder={this.handleValidOrder} isSubscription={!!this.props.subscription} redirect />

                                                <div className="w-75 mx-auto text-center text-primary">
                                                    <FormattedHTMLMessage id="cartLegalMentionText" defaultMessage="By validating your order you acknowledge having read the <a href='{link}' target='_blank'>GTC</a> and you accept the terms" values={{ link: this.getLegalConditionsUrl() }} />
                                                </div>
                                            </div>
                                        )
                                        : (
                                            <Fragment>
                                                {
                                                    (this.state.isCreatingOrder || this.state.isProcessingPayment)
                                                    ? (
                                                        <div className="h4 mt-3 text-center text-success">
                                                            <FontAwesome name="spinner" spin />{' '}
                                                            {this.state.isCreatingOrder && <FormattedMessage id="orderCreationProcessing" defaultMessage="We are creating your order..." />}
                                                            {this.state.isProcessingPayment && <FormattedMessage id="orderPaymentProcessing" defaultMessage="We are collecting data for your payment..." />}
                                                        </div>
                                                    )
                                                    : (
                                                        <Fragment>
                                                            <div className="mt-3 py-3 d-flex justify-content-center">
                                                                <Button color="warning" size="lg" onClick={this.handleValidOrder}><FormattedMessage id="cartPayButton" defaultMessage="Make the payment" /></Button>
                                                            </div>
                                                            <div className="w-75 mx-auto text-center text-primary">
                                                                <FormattedHTMLMessage id="cartLegalMentionText" defaultMessage="By validating your order you acknowledge having read the <a href='{link}' target='_blank'>GTC</a> and you accept the terms" values={{ link: this.getLegalConditionsUrl() }} />
                                                            </div>
                                                        </Fragment>
                                                    )
                                                }
                                            </Fragment>
                                        )
                                    }

                                    {this.state.order && this.state.payment && (
                                        <Fragment>
                                            {this.renderOrderPaymentProcessor()}
                                        </Fragment>
                                    )}
                                </Fragment>
                            )
                            : <strong className="d-block text-danger text-center"><FontAwesome name="exclamation-circle" /> <FormattedMessage id="cartNotValidError" defaultMessage="Check your cart before going to next step!" /></strong>
                        }
                    </CardBody>
                </Card>
            </Fragment>
        );
    }
}

function mapStateToProps(store, props) {
    const user = getLoggedUser(store);
    const shipping = getCartShipping(store);
    return {
        user,
        items: getCartItems(store),
        invoiceAddress: getInvoiceAddress(store, user && user._id) || (shipping || {}).address || null,
        shipping,
        shippingFee: getShippingFeeByAddress(store, (shipping || {}).address),
        voucher: getCartVoucher(store),
        subscription: getCartSubscription(store),
        isCartValid: isCartValid(store),
        cartTotals: getCartTotals(store),
        currency: store.currency.currency,
    };
}

CartPayment.defaultProps = {
    user: null,
    items: [],
    invoiceAddress: null,
    shipping: null,
    shippingFee: null,
    voucher: null,
    subscription: null,
    isCartValid: false,
    cartTotals: {},
    currency: '',
};

CartPayment.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    user: PropTypes.object,
    items: PropTypes.arrayOf(PropTypes.object),
    invoiceAddress: PropTypes.object,
    shipping: PropTypes.object,
    shippingFee: PropTypes.object,
    voucher: PropTypes.object,
    subscription: PropTypes.object,
    isCartValid: PropTypes.bool,
    cartTotals: PropTypes.object,
    currency: PropTypes.string,
    goToNextStep: PropTypes.func.isRequired,
};


export default connect(mapStateToProps)(injectIntl(CartPayment));
